import { useClients } from "@/hooks/useClients";
import { useEmploymentTypes } from "@/hooks/useEmploymentTypes";
import { JobCardV2 } from "@/ui/JobCardV2";
import JobSearch from "@sourceflow-uk/job-search";
import "@sourceflow-uk/job-search/dist/bundle.css";
import Team from "@sourceflow/team.json";
import cn from "classnames";
import { useCallback } from "react";
import { Alert, Container, FormCheck } from "react-bootstrap";

import RoleType from "@sourceflow/jobs-employment_type.json";
import Languages from "@sourceflow/jobs-Languages.json";
import Location from "@sourceflow/jobs-Location.json";
import Regions from "@sourceflow/jobs-Regions.json";
import Sectors from "@sourceflow/jobs-Sectors.json";
import Skills from "@sourceflow/jobs-Skills.json";
import WorkingOptions from "@sourceflow/jobs-working_options.json";
import Salary from "@sourceflow/salary.json";

import classes from "./styles.module.scss";

const CLIENT_CATEGORY_ID = "d1ed5b68-ab9f-483d-bef0-8c846265c7eb";

export default function ({ className = "pb-5 pb-lg-6", filters = true, prefilters = {} }) {
  const employment_types = useEmploymentTypes();
  const clients = useClients();

  const searchResults = useCallback(
    ({ results, total_size }) => {
      if (!results || results.length === 0) {
        return <Alert>No jobs exist matching your query.</Alert>;
      }

      // Featured jobs are pushed to the top of whatever the candidate is
      // currently looking at. The search API has no sort parameter, so this
      // orders the results already loaded into the page.
      const ordered = [...results].sort(
        (a, b) => Number(Boolean(b.job?.featured)) - Number(Boolean(a.job?.featured)),
      );

      return (
        <>
          {total_size !== undefined && (
            <p className={classes.count}>
              <strong>{total_size}</strong> {total_size === 1 ? "job" : "jobs"} found
            </p>
          )}
          <div className={classes.results}>
            {ordered.map(({ job }) => {
              const json = JSON.stringify(job.categories);
              const employment_type = employment_types.find((i) => json.includes(i.id));
              const [client] =
                job.categories.find((category) => category.id === CLIENT_CATEGORY_ID)?.values ?? [];

              return (
                <JobCardV2
                  key={job.id}
                  {...job}
                  job_client_logo={clients.find((i) => i.id === client?.id)?.logo || ""}
                  employment_type={employment_type}
                  location={job.addresses[0]}
                />
              );
            })}
          </div>
        </>
      );
    },
    [employment_types, clients],
  );

  return (
    <div className={cn(className, classes.JobsFeedV2)}>
      <Container fluid="xxl">
        <JobSearch
          translations={{
            "filters.toggleButtonText": "Filter jobs",
            "input.placeHolder": "Keyword or job title",
            "input.locationPlaceholder": "Location",
            "button.text": "Search",
            "searchResults.resultsTotalCount": "{{count}} jobs found",
          }}
          options={{
            jobsPerPage: 12,
            searchInput: {
              queryInput: true,
              locationInput: true,
              radiusInput: false,
              searchTotalCount: false,
            },
            searchSubmitButton: true,
            searchFilters: filters,
            searchFiltersOptions: {
              showResetSearchButton: true,
              topLevelCategoryOrder: ["WorkingOptions", "Role Type", "Salary", "Sectors", "Skills"],
              filterSelectedElement: <FormCheck checked={true} />,
              filterUnselectedElement: <FormCheck />,
              exclude: {
                [Team.id]: true,
                "de110faf-2fe4-4578-ae2d-bec2f8b5d23e": true,
                [CLIENT_CATEGORY_ID]: true,
                Location: true,
              },
              categorySort: {
                Skills: { key: "name", direction: "asc" },
                Sectors: { key: "name", direction: "asc" },
                [`Role Type`]: { key: "name", direction: "asc" },
                [`Working Options`]: { key: "name", direction: "asc" },
                Salary: function (a, b) {
                  const _a = Salary.category_values.find((i) => i.id === a.id);
                  const _b = Salary.category_values.find((i) => i.id === b.id);

                  if (_a._order > _b._order) return 1;
                  if (_a._order < _b._order) return -1;
                  return 0;
                },
              },
              // The sidebar has room to show the categories open, unlike the
              // dropdown-style filter bar on the live /jobs/ page.
              initialFilterOpenState: true,
              collapseFilterOnChange: false,
            },
            searchResultsOptions: {
              resultCategories: false,
            },
            urlFilters: {
              mode: "active",
              jobCategories: [
                RoleType,
                Languages,
                Location,
                Regions,
                Salary,
                Sectors,
                Skills,
                WorkingOptions,
              ],
              changeURLOnFilterChange: true,
            },
            prefilters,
            searchResults,
          }}
        />
      </Container>
    </div>
  );
}
